import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, FileText, Download, Calendar, User, Clock, AlertTriangle } from "lucide-react";
import { Layout } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { apiRequest, formatDate } from "../lib/utils";
import { toast } from "../components/ToastProvider";
import { AdminPasswordDialog } from "../components/AdminPasswordDialog";

const PERIODICIDADES = {
  mensal: "Mensal",
  trimestral: "Trimestral",
  semestral: "Semestral",
  anual: "Anual",
  bianual: "Bianual",
};

export const DocumentoDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [documento, setDocumento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchDocumento();
  }, [id]);

  const fetchDocumento = async () => {
    try {
      const data = await apiRequest(`/documentos/${id}`);
      setDocumento(data);
    } catch (error) {
      toast.error("Erro ao carregar documento");
      navigate("/documentos");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiRequest(`/documentos/${id}`, { method: "DELETE" });
      toast.success("Documento excluido com sucesso!");
      navigate("/documentos");
    } catch (error) {
      toast.error("Erro ao excluir documento");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6 max-w-4xl">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (!documento) {
    return (
      <Layout>
        <div className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900">Documento não encontrado</h3>
          <Button asChild className="mt-4">
            <Link to="/documentos">Voltar para lista</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  // Calcular dias até vencimento
  const hoje = new Date();
  const vencimento = new Date(documento.data_proximo_vencimento);
  const diasAteVencimento = Math.ceil((vencimento - hoje) / (1000 * 60 * 60 * 24));

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl animate-slide-in" data-testid="documento-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/documentos">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                {documento.nome_documento}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge status={documento.status} />
                <span className="text-slate-500">•</span>
                <span className="text-sm text-slate-500">{PERIODICIDADES[documento.periodicidade]}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to={`/documentos/${id}/editar`}>
                <Pencil className="w-4 h-4 mr-2" />
                Editar
              </Link>
            </Button>
            <Button 
              variant="outline" 
              className="text-red-600 hover:bg-red-50"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </div>
        </div>

        {/* Alerta de Vencimento */}
        {diasAteVencimento <= 60 && (
          <div className={`p-4 rounded-lg flex items-center gap-3 ${
            diasAteVencimento <= 0 ? 'bg-red-50 text-red-800' :
            diasAteVencimento <= 7 ? 'bg-orange-50 text-orange-800' :
            diasAteVencimento <= 30 ? 'bg-yellow-50 text-yellow-800' :
            'bg-blue-50 text-blue-800'
          }`}>
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">
              {diasAteVencimento <= 0 
                ? `Documento vencido ha ${Math.abs(diasAteVencimento)} dia(s)!`
                : `Documento vence em ${diasAteVencimento} dia(s)`
              }
            </span>
          </div>
        )}

        {/* Informações */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informações Gerais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Próximo Vencimento</p>
                  <p className="font-medium">{formatDate(documento.data_proximo_vencimento)}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Última Execucao</p>
                  <p className="font-medium">
                    {documento.data_ultima_execucao ? formatDate(documento.data_ultima_execucao) : "Não informada"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Responsável</p>
                  <p className="font-medium">{documento.responsavel || "Não informado"}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Observacoes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 whitespace-pre-wrap">
                {documento.observacoes || "Nenhuma observacao registrada."}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Arquivo */}
        {documento.arquivo_url && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Arquivo Anexado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <FileText className="w-10 h-10 text-teal-600" />
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{documento.arquivo_nome || "Documento"}</p>
                  <p className="text-sm text-slate-500">Clique para visualizar ou baixar</p>
                </div>
                <Button asChild>
                  <a href={documento.arquivo_url} target="_blank" rel="noopener noreferrer">
                    <Download className="w-4 h-4 mr-2" />
                    Baixar
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Admin Password Dialog para exclusão */}
        <AdminPasswordDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          onConfirm={handleDelete}
          title="Excluir Documento"
          description={`Tem certeza que deseja excluir "${documento.nome_documento}"? Esta ação não pode ser desfeita.`}
        />
      </div>
    </Layout>
  );
};

export default DocumentoDetail;
